import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { TokenStorage } from '../../services/api';
import {
  Plus, CheckCircle2, AlertTriangle, Eye, Download,
  FileText, Percent, ShieldAlert,
  TrendingUp, TrendingDown, ArrowRight, CalendarClock,
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import styles from '../../styles/Dashboardhome.module.scss';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';

const BASE_URL = process.env.REACT_APP_API_URL || 'https://code.mannaplag.mannaanalytics.com/api';

interface DashboardStats {
  plagiarism_remaining: number;
  ai_remaining: number;
  papers_remaining: number;
  papers_per_month: number;
  papers_used: number;
  credits_reset_date: string | null;
  checks_this_month: number;
  plagiarism_checks_this_month: number;
  ai_checks_this_month: number;
  avg_similarity: number;
  avg_similarity_trend: number;
  ai_flags: number;
  total_documents: number;
  current_plan: string;
}

interface FeedItem {
  id: string;
  title: string;
  file_name: string;
  overall_similarity: number;
  submitted_at: string;
  type: 'plagiarism' | 'ai' | 'failed';
  report_id?: string;
  error?: string;
}

interface TrendPoint {
  month: string;
  avgSimilarity: number | null;
  aiFlags: number;
}

// ── /submissions/usage-history/?months=6 — real per-month quota data ──
interface UsageHistoryMonth {
  month: string;
  year: string;
  month_key: string;
  is_current: boolean;
  plag_count: number;
  plag_quota: number;
  plag_remaining: number | null;
  ai_count: number;
  ai_quota: number;
  avg_similarity: number;
  quota_full: boolean;
}

interface UsageHistoryResponse {
  plan_name: string;
  papers_per_month: number;
  ai_per_month: number;
  months: UsageHistoryMonth[];
}

async function apiFetch(endpoint: string) {
  const token = TokenStorage.getAccess();
  const res = await fetch(`${BASE_URL}${endpoint}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`${res.status}`);
  return res.json();
}

function formatRelative(iso: string) {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffMs = now - then;
  const hr = Math.floor(diffMs / 3_600_000);
  if (hr < 1) return 'Just now';
  if (hr < 24) return `${hr} hour${hr === 1 ? '' : 's'} ago`;
  const days = Math.floor(hr / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

// ── Real-data helpers for the Integrity Trends chart ──────
function monthKey(iso: string) {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function monthLabel(key: string) {
  const [y, m] = key.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
}

function quarterKey(key: string) {
  const [y, m] = key.split('-').map(Number);
  return `${y}-Q${Math.ceil(m / 3)}`;
}

function average(nums: number[]) {
  return nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : 0;
}

export default function DashboardHome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const firstName = user?.full_name?.split(' ')[0] || 'there';

  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [feed, setFeed] = useState<FeedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [/* derived */, setDerived] = useState({ totalDocuments: 0, avgSimilarity: 0, aiFlags: 0 });
  const [trends, setTrends] = useState<TrendPoint[]>([]);
  const [quarterChange, setQuarterChange] = useState<number | null>(null);
  const [usageHistory, setUsageHistory] = useState<UsageHistoryResponse | null>(null);

  useEffect(() => {
    async function load() {
      try {
        const [statsData, plagData, aiData, allSubmissions, usageData] = await Promise.all([
          apiFetch('/submissions/dashboard/stats/'),
          apiFetch('/submissions/reports/').catch(() => []),
          apiFetch('/checker/list/').catch(() => []),
          apiFetch('/submissions/').catch(() => []),
          apiFetch('/submissions/usage-history/?months=6').catch(() => null),
        ]);

        setStats(statsData);
        setUsageHistory(usageData);

        const plagItems: FeedItem[] = (Array.isArray(plagData) ? plagData : plagData.results || [])
          .map((r: any) => ({
            id: String(r.id),
            title: r.title || r.file_name || 'Untitled',
            file_name: r.file_name || '',
            overall_similarity: r.overall_similarity ?? 0,
            submitted_at: r.submitted_at || r.generated_at || '',
            type: 'plagiarism' as const,
            report_id: String(r.id),
          }));

        const aiItems: FeedItem[] = (Array.isArray(aiData) ? aiData : aiData.results || [])
          .filter((r: any) => r.status === 'complete')
          .map((r: any) => ({
            id: String(r.id),
            title: r.title || r.file_name || 'Untitled',
            file_name: r.file_name || '',
            overall_similarity: r.overall_ai_score ?? 0,
            submitted_at: r.created_at || r.updated_at || '',
            type: 'ai' as const,
          }));

        const completedIds = new Set<string>([
          ...plagItems.map(i => i.id),
          ...aiItems.map(i => i.id),
        ]);

        const failedItems: FeedItem[] = (Array.isArray(allSubmissions) ? allSubmissions : allSubmissions.results || [])
          .filter((r: any) => r.status === 'failed' && !completedIds.has(String(r.id)))
          .map((r: any) => ({
            id: String(r.id),
            title: r.title || r.file_name || 'Untitled',
            file_name: r.file_name || '',
            overall_similarity: 0,
            submitted_at: r.submitted_at || '',
            type: 'failed' as const,
            error: r.error || 'Processing failed',
          }));

        const combined = [...plagItems, ...aiItems, ...failedItems]
          .filter(r => r.submitted_at)
          .sort((a, b) => new Date(b.submitted_at).getTime() - new Date(a.submitted_at).getTime())
          .slice(0, 10);

        setFeed(combined);

        // const allSubmissionsArr = Array.isArray(allSubmissions) ? allSubmissions : allSubmissions.results || [];

        const avgSimilarity = plagItems.length
          ? Math.round(average(plagItems.map(i => i.overall_similarity || 0)) * 10) / 10
          : 0;

        const aiFlags =
          plagItems.filter(i => (i.overall_similarity || 0) > 15).length +
          aiItems.filter(i => (i.overall_similarity || 0) > 30).length;

        setDerived({ totalDocuments: statsData?.total_documents ?? 0, avgSimilarity, aiFlags });

        const monthMap = new Map<string, { similarities: number[]; flagged: number }>();
        const touchMonth = (key: string) => {
          if (!monthMap.has(key)) monthMap.set(key, { similarities: [], flagged: 0 });
          return monthMap.get(key)!;
        };

        plagItems
          .filter(i => i.submitted_at)
          .forEach(i => {
            const g = touchMonth(monthKey(i.submitted_at));
            g.similarities.push(i.overall_similarity || 0);
            if ((i.overall_similarity || 0) > 15) g.flagged += 1;
          });

        aiItems
          .filter(i => i.submitted_at)
          .forEach(i => {
            const g = touchMonth(monthKey(i.submitted_at));
            if ((i.overall_similarity || 0) > 30) g.flagged += 1;
          });

        const sortedMonthKeys = Array.from(monthMap.keys()).sort();
        const trendPoints: TrendPoint[] = sortedMonthKeys.map(key => {
          const g = monthMap.get(key)!;
          return {
            month: monthLabel(key),
            avgSimilarity: g.similarities.length
              ? Math.round(average(g.similarities) * 10) / 10
              : null,
            aiFlags: g.flagged,
          };
        });
        setTrends(trendPoints);

        const quarterMap = new Map<string, number[]>();
        plagItems
          .filter(i => i.submitted_at)
          .forEach(i => {
            const qk = quarterKey(monthKey(i.submitted_at));
            if (!quarterMap.has(qk)) quarterMap.set(qk, []);
            quarterMap.get(qk)!.push(i.overall_similarity || 0);
          });

        const sortedQuarterKeys = Array.from(quarterMap.keys()).sort();
        if (sortedQuarterKeys.length >= 2) {
          const lastQ = sortedQuarterKeys[sortedQuarterKeys.length - 1];
          const prevQ = sortedQuarterKeys[sortedQuarterKeys.length - 2];
          const lastAvg = average(quarterMap.get(lastQ)!);
          const prevAvg = average(quarterMap.get(prevQ)!);
          if (prevAvg > 0) {
            setQuarterChange(Math.round(((prevAvg - lastAvg) / prevAvg) * 100));
          } else {
            setQuarterChange(null);
          }
        } else {
          setQuarterChange(null);
        }
      } catch (e) {
        setError('Could not load dashboard data.');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  if (loading) {
    return (
      <div className={styles.centerScreen}>
        <div className={styles.spinner} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.centerScreen}>
        <div className={styles.errorBox}>
          <AlertTriangle size={36} color="#f87171" />
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  const resetDate = stats?.credits_reset_date;
  const planName = usageHistory?.plan_name || stats?.current_plan || 'Free';
  const trendVal = stats?.avg_similarity_trend ?? 0;
  const TrendIcon = trendVal < 0 ? TrendingDown : TrendingUp;
  const trendColor = trendVal < 0 ? '#16a34a' : trendVal > 0 ? '#dc2626' : '#9ca3af';
  const trendStr = trendVal === 0 ? '0%' : `${Math.abs(trendVal)}%`;

  const currentMonth = usageHistory?.months.find(m => m.is_current) ?? null;

  const plagQuota = currentMonth?.plag_quota ?? stats?.papers_per_month ?? 0;
  // const plagUsed = currentMonth?.plag_count ?? stats?.plagiarism_checks_this_month ?? 0;
  // const papersRemaining = /* currentMonth?.plag_remaining ?? stats?.papers_remaining ?? Math.max(plagQuota - plagUsed, 0) */ stats?.plagiarism_remaining;

  // ai_per_month is a REAL field from usage-history — no more guessing.
  // const aiQuota = currentMonth?.ai_quota ?? usageHistory?.ai_per_month ?? null;
  // const aiUsed = currentMonth?.ai_count ?? stats?.ai_checks_this_month ?? 0;
  // const aiCreditsLeft = aiQuota !== null ? Math.max(aiQuota - aiUsed, 0) : null;

  // const plagPct = plagQuota > 0 ? Math.min((plagUsed / plagQuota) * 100, 100) : 0;
  // const aiPct = aiQuota ? Math.min((aiUsed / aiQuota) * 100, 100) : 0;

  // Bar chart data straight from /submissions/usage-history/, oldest → newest.
  const usageChartData = (usageHistory?.months ?? [])
    .slice()
    .sort((a, b) => a.month_key.localeCompare(b.month_key))
    .map(m => ({
      month: m.month.slice(0, 3).toUpperCase(),
      Plagiarism: m.plag_count,
      'AI Checks': m.ai_count,
    }));

  return (
    <div className={styles.page}>
      <div className={styles.grid}>

        {/* ── Header ─────────────────────────────────── */}
        <div className={styles.header} style={{ gridArea: 'header' }}>
          <div>
            <p className={styles.hello}>Hello, Welcome back</p>
            <h1 className={styles.welcome}>{firstName}</h1>
          </div>
        </div>

        {/* ── Plan card ──────────────────────────────── */}
        <div className={styles.planCard} style={{ gridArea: 'plan' }}>
          <div className={styles.planTop}>
            <span className={styles.planBadge}>{planName.toUpperCase()} PLAN</span>
          </div>
          <div className={styles.planRow}>
            <span className={styles.planRowLabel}>Papers Remaining</span>
            <span className={styles.planRowValue}>{stats?.plagiarism_remaining} / {plagQuota}</span>
          </div>
          <div className={styles.planRow}>
            <span className={styles.planRowLabel}>AI Analysis Credits</span>
            <span className={styles.planRowValue}>
              {stats?.ai_remaining != null ? (
                <>
                  {stats.ai_remaining}&nbsp;&nbsp;Left
                </>
              ) : (
                '—'
              )}
            </span>
          </div>
          <div className={styles.planFooter}>
            <span className={styles.planRenews}>
              <CalendarClock size={13} />
              {resetDate ? `Renews ${resetDate}` : 'Renews next month'}
            </span>
            <Link to="/choose-plan" state={{ upgradeOnly: true }} className={styles.manageLink}>Manage Plan</Link>
          </div>
        </div>

        {/* ── Mini stats ─────────────────────────────── */}
        <div className={styles.statCard} style={{ gridArea: 'stat1' }}>
          <div className={styles.statHead}>
            <p className={styles.statLabel}>Avg Similarity</p>
            <div className={styles.statIcon} style={{ background: '#e8edf4' }}>
              <Percent size={15} color="#003c6c" />
            </div>
          </div>
          <span className={styles.statValue}>{stats?.avg_similarity}%</span>
          <div className={styles.statDivider} />
          <span className={styles.statTrend} style={{ color: trendColor }}>
            <TrendIcon size={12} /> {trendStr} vs last month
          </span>
        </div>

        <div className={styles.statCard} style={{ gridArea: 'stat2' }}>
          <div className={styles.statHead}>
            <p className={styles.statLabel}>AI Flags</p>
            <div className={styles.statIcon} style={{ background: '#e8edf4' }}>
              <ShieldAlert size={15} color="#003c6c" />
            </div>
          </div>
          <span className={styles.statValue}>{stats?.ai_flags}</span>
          <div className={styles.statDivider} />
          <span className={styles.statSub}>this month</span>
        </div>

        <div className={styles.statCard} style={{ gridArea: 'stat3' }}>
          <div className={styles.statHead}>
            <p className={styles.statLabel}>Total Docs</p>
            <div className={styles.statIcon} style={{ background: '#e8edf4' }}>
              <FileText size={15} color="#003c6c" />
            </div>
          </div>
          <span className={styles.statValue}>{stats?.total_documents}</span>
          <div className={styles.statDivider} />
          <span className={styles.statSub}>vs last month</span>
        </div>

        {/* ── Recent activity ────────────────────────── */}
        <div className={styles.activityCard} style={{ gridArea: 'activity' }}>
          <div className={styles.panelHeadRow}>
            <p className={styles.panelTitle}>Recent Integrity Scans</p>
          </div>

          {feed.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyIconWrap}>
                <FileText size={22} color="#9ca3af" />
              </div>
              <p className={styles.emptyTitle}>No checks yet</p>
              <p className={styles.emptySub}>Your analysis history will appear here once you start a check.</p>
              <Link to="/dashboard/plagiarism" className={styles.emptyCta}>
                <Plus size={15} /> New Check
              </Link>
            </div>
          ) : (
            <div className={styles.feedList}>
              {feed.map(item => {
                const score = item.overall_similarity ?? 0;
                const isFailed = item.type === 'failed';
                const flagged = !isFailed && (item.type === 'plagiarism' ? score > 15 : score > 30);
                const reportPath = item.type === 'plagiarism'
                  ? `/reports/${item.report_id || item.id}`
                  : item.type === 'ai'
                    ? `/dashboard/ai-results/${item.id}`
                    : '/dashboard/plagiarism';

                return (
                  <div key={`${item.type}-${item.id}`} className={styles.feedRow} onClick={() => navigate(reportPath)}>
                    <div className={`${styles.feedStatusIcon} ${isFailed || flagged ? styles.feedStatusWarn : styles.feedStatusOk}`}>
                      {isFailed || flagged ? <AlertTriangle size={16} /> : <CheckCircle2 size={16} />}
                    </div>
                    <div className={styles.feedInfo}>
                      <p className={styles.feedName}>{item.title || item.file_name}</p>
                      <p className={styles.feedMeta}>
                        {isFailed
                          ? `Failed — ${item.error}`
                          : item.type === 'plagiarism' ? `Plagiarism: ${score}%` : `AI: ${Math.round(score)}%`}
                      </p>
                    </div>
                    <div className={styles.feedRight}>
                      <span className={styles.feedTime}>{formatRelative(item.submitted_at)}</span>
                      {isFailed ? <span className={styles.feedRetry}>Retry</span> : flagged ? <Eye size={15} /> : <Download size={15} />}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Integrity Trends ───────────────────────── */}
        <div className={styles.trendsCard} style={{ gridArea: 'trends' }}>
          <div className={styles.trendsHeader}>
            <p className={styles.trendsTitle}>Integrity Trends</p>
            <div className={styles.trendsLegend}>
              <span className={styles.legendItem}>
                <span className={styles.legendDotSimilarity} /> Avg Similarity
              </span>
              <span className={styles.legendItem}>
                <span className={styles.legendDotFlags} /> AI Flags
              </span>
            </div>
          </div>

          {trends.length === 0 ? (
            <div className={styles.trendsEmpty}>
              <p>Not enough data yet — trends will appear here once you have completed checks.</p>
            </div>
          ) : (
            <div className={styles.trendsChartWrap}>
              <ResponsiveContainer width="100%" height={140}>
                <LineChart data={trends} margin={{ top: 8, right: 4, bottom: 0, left: 4 }}>
                  <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="#eef2f7" />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 9, fill: '#9ca3af', fontWeight: 600 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis yAxisId="similarity" hide domain={[0, 100]} />
                  <YAxis yAxisId="flags" orientation="right" hide domain={[0, 'auto']} />
                  <Tooltip
                    formatter={(value?: ValueType, name?: NameType) => [
                      name === "avgSimilarity" ? `${value ?? 0}%` : value ?? 0,
                      name === "avgSimilarity" ? "Avg Similarity" : "AI Flags",
                    ]}
                  />
                  <Line
                    yAxisId="similarity"
                    type="monotone"
                    dataKey="avgSimilarity"
                    stroke="#003c6c"
                    strokeWidth={2}
                    strokeDasharray="4 3"
                    dot={{ r: 3, fill: '#003c6c' }}
                    connectNulls
                    label={(props: any) => {
                      const { x, y, value } = props;
                      if (value === null || value === undefined) return <></>;
                      return (
                        <text x={x} y={y - 8} textAnchor="middle" fontSize={10} fontWeight={700} fill="#003c6c">
                          {value}%
                        </text>
                      );
                    }}
                  />
                  <Line
                    yAxisId="flags"
                    type="monotone"
                    dataKey="aiFlags"
                    stroke="#14b8a6"
                    strokeWidth={2}
                    dot={{ r: 3, fill: '#14b8a6' }}
                    label={(props: any) => {
                      const { x, y, value } = props;
                      if (value === null || value === undefined) return <></>;
                      return (
                        <text x={x} y={y + 16} textAnchor="middle" fontSize={10} fontWeight={700} fill="#14b8a6">
                          {value}
                        </text>
                      );
                    }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {quarterChange !== null ? (
            <p className={styles.trendsSummary}>
              Overall integrity score has {quarterChange >= 0 ? 'improved' : 'declined'} by {Math.abs(quarterChange)}% compared to the previous quarter.
            </p>
          ) : (
            <p className={styles.trendsSummary}>
              Quarter-over-quarter comparison will appear once you have checks spanning more than one quarter.
            </p>
          )}
        </div>

        {/* ── Upload History — real data from /submissions/usage-history/ ── */}
        <div className={styles.trendsCard} style={{ gridArea: 'chart2' }}>
          <div className={styles.trendsHeader}>
            <p className={styles.trendsTitle}>Upload History</p>
            <div className={styles.trendsLegend}>
              <span className={styles.legendItem}>
                <span className={styles.legendDotPlag} /> Plagiarism
              </span>
              <span className={styles.legendItem}>
                <span className={styles.legendDotAi} /> AI Checks
              </span>
            </div>
          </div>

          {usageChartData.length === 0 ? (
            <div className={styles.trendsEmpty}>
              <p>Upload history will appear here once usage data is available.</p>
            </div>
          ) : (

            <div className={styles.trendsChartWrap}>
              <ResponsiveContainer width="100%" height={140}>
                <BarChart data={usageChartData} margin={{ top: 8, right: 4, bottom: 0, left: 4 }} barGap={4}>
                  <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="#eef2f7" />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 9, fill: '#9ca3af', fontWeight: 600 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    hide
                    domain={[0, (dataMax: number) => Math.max(dataMax, 4)]}
                    allowDecimals={false}
                  />
                  <Tooltip
                    contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #e8edf4' }}
                    cursor={{ fill: 'rgba(0,60,108,0.04)' }}
                  />
                  <Bar dataKey="Plagiarism" fill="#bfdbfe" radius={[4, 4, 0, 0]} maxBarSize={16} />
                  <Bar dataKey="AI Checks" fill="#99f6e4" radius={[4, 4, 0, 0]} maxBarSize={16} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          <p className={styles.trendsSummary}>
            {usageHistory
              ? `${usageHistory.plan_name} plan — ${usageHistory.papers_per_month} paper${usageHistory.papers_per_month === 1 ? '' : 's'} & ${usageHistory.ai_per_month} AI checks per month.`
              : 'Monthly Plagiarism and AI check counts from your upload history.'}
          </p>
        </div>
      </div>

      {/* ── Recent Activity — full-width history table below everything ── */}
      <div className={styles.historySection}>
        <div className={styles.panelHeadRow}>
          <p className={styles.panelTitle}>Recent Activity</p>
          <Link to="/dashboard/usage" className={styles.viewAllLink}>View all <ArrowRight size={13} /></Link>
        </div>

        {feed.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIconWrap}>
              <FileText size={22} color="#9ca3af" />
            </div>
            <p className={styles.emptyTitle}>No history yet</p>
            <p className={styles.emptySub}>Everything you check will show up here.</p>
          </div>
        ) : (
          <div className={styles.historyTable}>
            <div className={styles.historyHeadRow}>
              <span>Document</span>
              <span>Type</span>
              <span>Score</span>
              <span>Date</span>
              <span>Status</span>
            </div>
            {feed.map(item => {
              const score = item.overall_similarity ?? 0;
              const isFailed = item.type === 'failed';
              const flagged = !isFailed && (item.type === 'plagiarism' ? score > 15 : score > 30);
              const reportPath = item.type === 'plagiarism'
                ? `/reports/${item.report_id || item.id}`
                : item.type === 'ai'
                  ? `/dashboard/ai-results/${item.id}`
                  : '/dashboard/plagiarism';

              return (
                <div
                  key={`history-${item.type}-${item.id}`}
                  className={styles.historyRow}
                  onClick={() => navigate(reportPath)}
                >
                  <span className={styles.historyDoc}>{item.title || item.file_name}</span>
                  <span className={styles.historyType}>{item.type === 'plagiarism' ? 'Plagiarism' : item.type === 'ai' ? 'AI Check' : 'Failed'}</span>
                  <span className={styles.historyScore}>{isFailed ? '—' : `${score}%`}</span>
                  <span className={styles.historyDate}>{formatRelative(item.submitted_at)}</span>
                  <span className={`${styles.historyBadge} ${isFailed || flagged ? styles.historyBadgeWarn : styles.historyBadgeOk}`}>
                    {isFailed ? 'Failed' : flagged ? 'Flagged' : 'Clear'}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}